import type { ReactNode } from "react";

import { faCircleQuestion } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import clsx from "clsx";
import styled, { useTheme } from "styled-components";

import { Tooltip } from "shared/atoms/Tooltip";
import { DARK_BLUE } from "theme/colors";

type Color = "blue" | "red" | "green" | "yellow" | "purple" | "teal" | "orange";

interface Props {
  bordered?: boolean;
  color?: Color;
  children?: ReactNode;
  className?: string;
  description?: string;
  icon: ReactNode;
  notifications?: number;
  skipPadding?: boolean;
  smallPadding?: boolean;
  title: string | ReactNode;
  toolbar?: ReactNode;
  tooltip?: string;
}

export const WidgetBox: React.FC<Props> = ({
  bordered,
  children,
  className,
  color = "blue",
  description,
  icon,
  notifications,
  skipPadding,
  smallPadding,
  title,
  toolbar,
  tooltip,
}) => {
  const theme = useTheme();
  return (
    <Container className={clsx({ "no-padding": skipPadding, "small-padding": smallPadding, bordered }, className)}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconWrapper $color={color}>{icon}</IconWrapper>

        <Header>
          <Stack flex={2}>
            {typeof title === "string" ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h4">{title}</Typography>
                {notifications !== undefined && <NotificationBadge>{notifications}</NotificationBadge>}
                {tooltip && (
                  <Tooltip title={tooltip} placement="bottom" arrow>
                    <FontAwesomeIcon icon={faCircleQuestion} style={{ color: DARK_BLUE[60], fontSize: 16 }} />
                  </Tooltip>
                )}
              </Stack>
            ) : (
              <Typography variant="h4" component="div">
                {title}
              </Typography>
            )}
            {description && (
              <Typography variant="subtitle1" color={theme.colors.redesign.db60} mt={0.5}>
                {description}
              </Typography>
            )}
          </Stack>

          {toolbar && <div>{toolbar}</div>}
        </Header>
      </Stack>
      {children}
    </Container>
  );
};

const Container = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 12px;
  background-color: ${props => props.theme.colors.white};
  border: none;

  &.small-padding {
    padding: 16px;
  }

  &.no-padding {
    padding: 0;
  }

  &.bordered {
    border: 1px solid ${props => props.theme.colors.redesign.db10};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
`;

const IconWrapper = styled.div<{ $color?: Color }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  font-size: 20px;
  background-color: ${props => props.theme.colors.redesign.b10};
  ${props =>
    props.$color === "blue" &&
    `
    background-color: ${props.theme.colors.redesign.b10};
    color: ${props.theme.colors.redesign.b100};
  `}
  ${props =>
    props.$color === "red" &&
    `
    background-color: ${props.theme.colors.redesign.r10};
    color: ${props.theme.colors.redesign.r100};
  `}
  ${props =>
    props.$color === "green" &&
    `
    background-color: ${props.theme.colors.redesign.g10};
    color: ${props.theme.colors.redesign.g100};
  `}
  ${props =>
    props.$color === "yellow" &&
    `
    background-color: ${props.theme.colors.redesign.y10};
    color: ${props.theme.colors.redesign.y100};
  `}
    ${props =>
    props.$color === "orange" &&
    `
    background-color: ${props.theme.colors.redesign.o10};
    color: ${props.theme.colors.redesign.o100};
  `}
  ${props =>
    props.$color === "purple" &&
    `
    background-color: ${props.theme.colors.redesign.p10};
    color: ${props.theme.colors.redesign.p100};
  `}
  ${props =>
    props.$color === "teal" &&
    `
    background-color: ${props.theme.colors.redesign.t10};
    color: ${props.theme.colors.redesign.t100};
  `}
`;

const NotificationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background-color: ${props => props.theme.colors.redesign.b10};
  color: ${props => props.theme.colors.redesign.b100};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;
